<div class="display-management-page">

    <!-- Site sidebar -->
    @if(!selectedSite()) {
    <aside class="site-sidebar">
        <div class="sidebar-header">
            <h2 class="sidebar-title">
                <mat-icon>location_city</mat-icon> Parking Sites
            </h2>
            <mat-form-field appearance="fill" class="search-field">
                <mat-label>Search sites</mat-label>
                <input matInput [(ngModel)]="siteSearch" placeholder="Search…" />
                <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>
        </div>

        @if (isLoadingSites()) {
        <div class="loading-center">
            <mat-spinner diameter="32" />
        </div>
        } @else {
        <mat-nav-list>
            @for (site of filteredSites; track site.id) {
            <mat-list-item [class.selected-site]="selectedSite()?.id === site.id" (click)="selectSite(site)">
                <mat-icon matListItemIcon>parking</mat-icon>
                <span matListItemTitle>{{ site.name }}</span>
                <span matListItemLine class="site-type-label">{{ site.type }}</span>
            </mat-list-item>
            } @empty {
            <p class="empty-hint">No sites found</p>
            }
        </mat-nav-list>
        }
    </aside>
    }
    <!-- Main content area -->
    <main class="display-area">

        @if (!selectedSite()) {
        <!-- No site selected -->
        <div class="no-selection-placeholder">
            <mat-icon>tv</mat-icon>
            <h3>Select a parking site</h3>
            <p>Choose a site from the sidebar to view and manage its displays.</p>
        </div>

        } @else if (templateDisplay()) {
        <!-- Template editor for a display -->
        <app-display-template-editor [display]="templateDisplay()!" [siteId]="selectedSite()?.id ?? null"
            (close)="closeTemplateEditor()" />

        } @else {
        <!-- Display list for selected site -->
        <div class="display-list-section">

            <div class="list-header">
                <!-- Back button -->
                <button mat-icon-button (click)="deselectSite()">
                    <mat-icon>arrow_back</mat-icon>
                </button>
                <div class="list-header-title">
                    <h2>{{ selectedSite()!.name }}</h2>
                    <span class="display-count">{{ displays().length }} display(s)</span>
                </div>
                <button mat-stroked-button (click)="loadDisplays(selectedSite()!.id!)">
                    <mat-icon>refresh</mat-icon> Refresh
                </button>
            </div>

            @if (loadingDisplays()) {
            <div class="loading-center">
                <mat-spinner diameter="48" />
            </div>
            } @else if (displays().length === 0) {
            <div class="empty-displays">
                <mat-icon>tv_off</mat-icon>
                <p>No displays found for this site.</p>
            </div>
            } @else {
            <div class="table-container">
                <table mat-table [dataSource]="displays()" class="displays-table">

                    <!-- Name + online status -->
                    <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef>Display</th>
                        <td mat-cell *matCellDef="let row">
                            <div class="display-name-cell">
                                <mat-icon class="display-icon">tv</mat-icon>
                                <span class="device-name">{{ row.deviceName }}</span>
                                @if (row.isOnline) {
                                <span class="badge badge-online">Online</span>
                                } @else {
                                <span class="badge badge-offline">Offline</span>
                                }
                            </div>
                        </td>
                    </ng-container>

                    <!-- Gate (device group) -->
                    <ng-container matColumnDef="gate">
                        <th mat-header-cell *matHeaderCellDef>Gate</th>
                        <td mat-cell *matCellDef="let row" class="gate-cell">
                            {{ getGateName(row.gateId) }}
                        </td>
                    </ng-container>

                    <!-- Current state -->
                    <ng-container matColumnDef="state">
                        <th mat-header-cell *matHeaderCellDef>State</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="state-chip state-{{ getStateColor(row.currentState) }}">
                                {{ row.currentState ?? '—' }}
                            </span>
                        </td>
                    </ng-container>

                    <!-- Active override -->
                    <ng-container matColumnDef="override">
                        <th mat-header-cell *matHeaderCellDef>Override</th>
                        <td mat-cell *matCellDef="let row" class="override-cell">
                            {{ row.activeOverride || '—' }}
                        </td>
                    </ng-container>

                    <!-- Last heartbeat -->
                    <ng-container matColumnDef="heartbeat">
                        <th mat-header-cell *matHeaderCellDef>Last Heartbeat</th>
                        <td mat-cell *matCellDef="let row" class="heartbeat-cell">
                            {{ row.lastHeartbeatAt ? (row.lastHeartbeatAt | date:'short') : '—' }}
                        </td>
                    </ng-container>

                    <!-- Row actions -->
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef>Actions</th>
                        <td mat-cell *matCellDef="let row">
                            <div class="action-buttons">

                                <button mat-icon-button
                                    [color]="row.currentState === DisplayState.Emergency ? 'warn' : ''"
                                    [matTooltip]="row.currentState === DisplayState.Emergency ? 'Clear Emergency' : 'Activate Emergency'"
                                    [disabled]="isActionBusy(row.displayId)" (click)="onEmergency(row)">
                                    <mat-icon>warning</mat-icon>
                                </button>

                                <button mat-icon-button
                                    [color]="row.currentState === DisplayState.Maintenance ? 'accent' : ''"
                                    [matTooltip]="row.currentState === DisplayState.Maintenance ? 'Clear Maintenance' : 'Activate Maintenance'"
                                    [disabled]="isActionBusy(row.displayId)" (click)="onMaintenance(row)">
                                    <mat-icon>build</mat-icon>
                                </button>

                                <button mat-icon-button matTooltip="Custom Message"
                                    [disabled]="isActionBusy(row.displayId)" (click)="openCustomMessage(row.displayId)">
                                    <mat-icon>message</mat-icon>
                                </button>

                                <button mat-icon-button matTooltip="Push State" [disabled]="isActionBusy(row.displayId)"
                                    (click)="openPushState(row.displayId)">
                                    <mat-icon>send</mat-icon>
                                </button>

                                <button mat-icon-button color="primary" matTooltip="Manage Template"
                                    (click)="openTemplateEditor(row)">
                                    <mat-icon>dashboard_customize</mat-icon>
                                </button>

                                <a mat-icon-button color="primary" matTooltip="Open on Edge Display"
                                    [href]="getEdgeUrl(row.displayId)" target="_blank" rel="noopener noreferrer">
                                    <mat-icon>open_in_new</mat-icon>
                                </a>

                                @if (isAdmin()) {
                                <button mat-icon-button matTooltip="Test Heartbeat"
                                    [disabled]="isActionBusy(row.displayId)" (click)="onHeartbeat(row.displayId)">
                                    <mat-icon>favorite</mat-icon>
                                </button>
                                }

                            </div>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns"
                        [class.row-emergency]="row.currentState === DisplayState.Emergency"
                        [class.row-maintenance]="row.currentState === DisplayState.Maintenance"></tr>
                </table>
            </div>

            <!-- Action form panel: Custom Message -->
            @if (customMessageDisplayId()) {
            <div class="action-form-panel">
                <div class="action-form-header">
                    <mat-icon>message</mat-icon>
                    <span>Custom Message — {{ customMessageDisplayId() }}</span>
                    <button mat-icon-button (click)="closeCustomMessage()">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
                <div class="action-form-body">
                    <mat-form-field appearance="fill" class="message-field">
                        <mat-label>Message (max 200 chars)</mat-label>
                        <textarea matInput [(ngModel)]="customMessageText" rows="2" maxlength="200"
                            placeholder="Enter message…"></textarea>
                        <mat-hint align="end">{{ customMessageText.length }}/200</mat-hint>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="ttl-field">
                        <mat-label>TTL (seconds, optional)</mat-label>
                        <input matInput type="number" [(ngModel)]="customMessageTtl" min="1" />
                    </mat-form-field>
                    <div class="form-actions">
                        <button mat-raised-button color="primary"
                            [disabled]="!customMessageText.trim() || isActionBusy(customMessageDisplayId()!)"
                            (click)="sendCustomMessage(customMessageDisplayId()!)">
                            Send
                        </button>
                        <button mat-stroked-button color="warn" [disabled]="isActionBusy(customMessageDisplayId()!)"
                            (click)="clearCustomMessage(customMessageDisplayId()!)">
                            Clear Active
                        </button>
                        <button mat-button (click)="closeCustomMessage()">Cancel</button>
                    </div>
                </div>
            </div>
            }

            <!-- Action form panel: Push State -->
            @if (pushStateDisplayId()) {
            <div class="action-form-panel">
                <div class="action-form-header">
                    <mat-icon>send</mat-icon>
                    <span>Push State — {{ pushStateDisplayId() }}</span>
                    <button mat-icon-button (click)="closePushState()">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
                <div class="action-form-body">
                    <mat-form-field appearance="fill">
                        <mat-label>Target State</mat-label>
                        <mat-select [(ngModel)]="pushStateValue">
                            @for (state of allDisplayStates; track state) {
                            <mat-option [value]="state">{{ state }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>

                    <div class="live-data-section">
                        <div class="live-data-header">
                            <span>Live Data — the values this state's layers read</span>
                            <button mat-button (click)="addLiveDataRow()">
                                <mat-icon>add</mat-icon> Add field
                            </button>
                        </div>

                        @if (suggestedKeys().length) {
                        <div class="live-data-suggestions">
                            <span class="live-data-suggestions-label">Keys this state is sent with:</span>
                            @for (key of suggestedKeys(); track key) {
                            <button type="button" class="key-chip" (click)="addSuggestedKey(key)">{{ key }}</button>
                            }
                        </div>
                        } @else if (pushStateValue) {
                        <p class="live-data-note">
                            This state carries no live data of its own. Any key you add is passed through to the
                            layers as-is.
                        </p>
                        }
                        @for (row of pushStateLiveData(); track $index) {
                        <div class="live-data-row">
                            <mat-form-field appearance="fill" class="live-data-key">
                                <mat-label>Key</mat-label>
                                <input matInput [ngModel]="row.key"
                                    (ngModelChange)="updateLiveDataKey($index, $event)" placeholder="e.g. plate" />
                            </mat-form-field>
                            <mat-form-field appearance="fill" class="live-data-value">
                                <mat-label>Value</mat-label>
                                <input matInput [ngModel]="row.value"
                                    (ngModelChange)="updateLiveDataValue($index, $event)" placeholder="e.g. W-12345" />
                            </mat-form-field>
                            <button mat-icon-button color="warn" matTooltip="Remove field"
                                (click)="removeLiveDataRow($index)">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                        }
                    </div>

                    <div class="form-actions">
                        <button mat-raised-button color="primary"
                            [disabled]="!pushStateValue || isActionBusy(pushStateDisplayId()!)"
                            (click)="sendPushState(pushStateDisplayId()!)">
                            Push
                        </button>
                        <button mat-button (click)="closePushState()">Cancel</button>
                    </div>
                </div>
            </div>
            }

            }
        </div>
        }

    </main>
</div>